import { supabase } from '../lib/supabase'
import type {
  AssignmentStatistics,
  OutstandingAssignmentDetail,
  OutstandingCustomerGroup,
  OutstandingReports,
  OutstandingSubcontractorGroup,
  PeriodStatistics,
} from '../types/statistics'

/**
 * Lock prices on assignment_articles for a given assignment.
 * Called at attestation time to freeze current prices so future
 * price list changes don't affect historical data.
 *
 * - If custom_price is NULL → set to customer price from price list
 * - If subcontractor_prices[tgId] is missing → set to TG price from price list
 */
export async function lockPricesForAssignment(assignmentId: string): Promise<void> {
  // 1. Get assignment with customer info
  const { data: assignment, error: assignmentError } = await supabase
    .from('assignments')
    .select('id, customer_id')
    .eq('id', assignmentId)
    .single()

  if (assignmentError || !assignment) {
    console.error('lockPrices: Could not fetch assignment', assignmentError)
    return
  }

  // 2. Get customer's price list
  const { data: customer } = await supabase
    .from('customers')
    .select('id, price_list_id')
    .eq('id', assignment.customer_id)
    .single()

  // 3. Get default price lists
  const { data: defaultPriceLists } = await supabase
    .from('price_lists')
    .select('id, type')
    .eq('is_default', true)
    .eq('is_active', true)

  const defaultCustomerPriceListId = defaultPriceLists?.find(pl => pl.type === 'customer')?.id
  const defaultSubcontractorPriceListId = defaultPriceLists?.find(pl => pl.type === 'subcontractor')?.id

  // Determine which customer price list to use
  const customerPriceListId = customer?.price_list_id || defaultCustomerPriceListId

  // 4. Get all TGs on this assignment with their price lists
  const { data: assignmentTgs } = await supabase
    .from('assignment_subcontractors')
    .select('subcontractor_id, subcontractors(id, price_list_id)')
    .eq('assignment_id', assignmentId)

  // Build map: tgId → priceListId
  const tgPriceListMap: Record<string, string | null> = {}
  for (const tg of assignmentTgs || []) {
    const sub = tg.subcontractors as any
    tgPriceListMap[tg.subcontractor_id] = sub?.price_list_id || null
  }

  // 5. Collect all price list IDs we need
  const allPriceListIds = new Set<string>()
  if (customerPriceListId) allPriceListIds.add(customerPriceListId)
  if (defaultSubcontractorPriceListId) allPriceListIds.add(defaultSubcontractorPriceListId)
  for (const plId of Object.values(tgPriceListMap)) {
    if (plId) allPriceListIds.add(plId)
  }

  // 6. Fetch all prices in one query
  const pricesByList: Record<string, Record<string, number>> = {}
  if (allPriceListIds.size > 0) {
    const { data: priceItems } = await supabase
      .from('price_list_articles')
      .select('price_list_id, article_id, price_excl_vat')
      .in('price_list_id', Array.from(allPriceListIds))

    for (const item of priceItems || []) {
      if (!pricesByList[item.price_list_id]) {
        pricesByList[item.price_list_id] = {}
      }
      pricesByList[item.price_list_id][item.article_id] = item.price_excl_vat
    }
  }

  // Helper: get customer price for an article
  const getCustomerPrice = (articleId: string): number | null => {
    if (customerPriceListId && pricesByList[customerPriceListId]?.[articleId] != null) {
      return pricesByList[customerPriceListId][articleId]
    }
    if (defaultCustomerPriceListId && pricesByList[defaultCustomerPriceListId]?.[articleId] != null) {
      return pricesByList[defaultCustomerPriceListId][articleId]
    }
    return null
  }

  // Helper: get TG price for an article
  const getTgPrice = (articleId: string, tgId: string): number | null => {
    const tgPlId = tgPriceListMap[tgId]
    if (tgPlId && pricesByList[tgPlId]?.[articleId] != null) {
      return pricesByList[tgPlId][articleId]
    }
    if (defaultSubcontractorPriceListId && pricesByList[defaultSubcontractorPriceListId]?.[articleId] != null) {
      return pricesByList[defaultSubcontractorPriceListId][articleId]
    }
    return null
  }

  // 7. Get assignment articles
  const { data: articles } = await supabase
    .from('assignment_articles')
    .select('id, article_id, custom_price, subcontractor_prices')
    .eq('assignment_id', assignmentId)

  if (!articles || articles.length === 0) return

  // 8. Update each article that needs price locking
  const tgIds = Object.keys(tgPriceListMap)

  for (const article of articles) {
    const updates: Record<string, unknown> = {}
    let needsUpdate = false

    // Lock customer price if not already set
    if (article.custom_price == null) {
      const customerPrice = getCustomerPrice(article.article_id)
      if (customerPrice != null) {
        updates.custom_price = customerPrice
        needsUpdate = true
      }
    }

    // Lock TG prices for each TG that doesn't have a price yet
    const existingTgPrices = (article.subcontractor_prices as Record<string, number>) || {}
    const newTgPrices = { ...existingTgPrices }
    let tgPricesChanged = false

    for (const tgId of tgIds) {
      if (existingTgPrices[tgId] == null) {
        const tgPrice = getTgPrice(article.article_id, tgId)
        if (tgPrice != null) {
          newTgPrices[tgId] = tgPrice
          tgPricesChanged = true
        }
      }
    }

    if (tgPricesChanged) {
      updates.subcontractor_prices = newTgPrices
      needsUpdate = true
    }

    if (needsUpdate) {
      await supabase
        .from('assignment_articles')
        .update(updates)
        .eq('id', article.id)
    }
  }
}

/**
 * Fetch statistics for completed assignments in a date range.
 */
export async function fetchPeriodStatistics(
  dateFrom: string,
  dateTo: string
): Promise<PeriodStatistics> {
  // 1. Get completed assignments in the period (including admin_reported_articles)
  const { data: assignments, error: assignmentsError } = await supabase
    .from('assignments')
    .select(`
      id,
      assignment_number,
      title,
      start_date,
      customer_id,
      admin_reported_articles,
      customers (
        id,
        full_name,
        price_list_id,
        provision_partner_id
      )
    `)
    .in('status', ['slutford', 'attesterad'])
    .gte('start_date', dateFrom)
    .lte('start_date', dateTo)
    .order('start_date', { ascending: true })

  if (assignmentsError) {
    console.error('Statistics: Error fetching assignments', assignmentsError)
    throw assignmentsError
  }

  if (!assignments || assignments.length === 0) {
    return emptyPeriod()
  }

  const assignmentIds = assignments.map(a => a.id)

  // 2. Get assignment_articles for all assignments
  const { data: assignmentArticles } = await supabase
    .from('assignment_articles')
    .select(`
      id,
      assignment_id,
      article_id,
      quantity,
      custom_price,
      subcontractor_prices,
      articles (
        id,
        is_provision_article
      )
    `)
    .in('assignment_id', assignmentIds)

  // 3. Get TG reports (reported_articles JSONB) - include is_admin to separate costs
  const { data: tgReports } = await supabase
    .from('assignment_subcontractors')
    .select(`
      assignment_id,
      subcontractor_id,
      reported_articles,
      subcontractors (
        id,
        name,
        price_list_id,
        is_admin
      )
    `)
    .in('assignment_id', assignmentIds)
    .in('tg_status', ['godkand', 'avsagt'])

  // 4. Get default price lists
  const { data: defaultPriceLists } = await supabase
    .from('price_lists')
    .select('id, type')
    .eq('is_default', true)
    .eq('is_active', true)

  const defaultCustomerPriceListId = defaultPriceLists?.find(pl => pl.type === 'customer')?.id
  const defaultSubcontractorPriceListId = defaultPriceLists?.find(pl => pl.type === 'subcontractor')?.id

  // 5. Collect all price list IDs we need
  const allPriceListIds = new Set<string>()
  if (defaultCustomerPriceListId) allPriceListIds.add(defaultCustomerPriceListId)
  if (defaultSubcontractorPriceListId) allPriceListIds.add(defaultSubcontractorPriceListId)

  // Customer price lists
  for (const a of assignments) {
    const customer = a.customers as any
    if (customer?.price_list_id) allPriceListIds.add(customer.price_list_id)
  }

  // TG price lists
  for (const tg of tgReports || []) {
    const sub = tg.subcontractors as any
    if (sub?.price_list_id) allPriceListIds.add(sub.price_list_id)
  }

  // 6. Fetch all prices in one query
  const pricesByList: Record<string, Record<string, number>> = {}
  if (allPriceListIds.size > 0) {
    const { data: priceItems } = await supabase
      .from('price_list_articles')
      .select('price_list_id, article_id, price_excl_vat')
      .in('price_list_id', Array.from(allPriceListIds))

    for (const item of priceItems || []) {
      if (!pricesByList[item.price_list_id]) {
        pricesByList[item.price_list_id] = {}
      }
      pricesByList[item.price_list_id][item.article_id] = item.price_excl_vat
    }
  }

  // 6b. Collect admin-reported article IDs and fetch their provision status
  const adminArticleIds = new Set<string>()
  for (const assignment of assignments) {
    const adminArticles = (assignment.admin_reported_articles as any[]) || []
    for (const ra of adminArticles) {
      if (ra.article_id && !ra.is_expense) {
        adminArticleIds.add(ra.article_id)
      }
    }
  }

  const adminArticleProvisionMap: Record<string, boolean> = {}
  if (adminArticleIds.size > 0) {
    const { data: adminArticleData } = await supabase
      .from('articles')
      .select('id, is_provision_article')
      .in('id', Array.from(adminArticleIds))

    for (const a of adminArticleData || []) {
      adminArticleProvisionMap[a.id] = a.is_provision_article || false
    }
  }

  // 7. Get provision partners
  const { data: partners } = await supabase
    .from('provision_partners')
    .select('id, percentage')
    .eq('is_active', true)

  const partnerMap: Record<string, number> = {}
  for (const p of partners || []) {
    partnerMap[p.id] = p.percentage
  }

  // 8. Build per-assignment statistics
  const result: AssignmentStatistics[] = []

  for (const assignment of assignments) {
    const customer = assignment.customers as any
    const customerPriceListId = customer?.price_list_id || defaultCustomerPriceListId

    // Helper: get customer price for an article
    const getCustomerPrice = (articleId: string): number => {
      if (customerPriceListId && pricesByList[customerPriceListId]?.[articleId] != null) {
        return pricesByList[customerPriceListId][articleId]
      }
      if (defaultCustomerPriceListId && pricesByList[defaultCustomerPriceListId]?.[articleId] != null) {
        return pricesByList[defaultCustomerPriceListId][articleId]
      }
      return 0
    }

    const myArticles = (assignmentArticles || []).filter(aa => aa.assignment_id === assignment.id)
    const myTgReports = (tgReports || []).filter(tg => tg.assignment_id === assignment.id)

    // Build a lookup: articleId → is_provision_article
    const articleProvisionMap: Record<string, boolean> = {}
    for (const aa of myArticles) {
      const article = aa.articles as any
      articleProvisionMap[aa.article_id] = article?.is_provision_article || false
    }

    // Helper: get customer price for an article (considering custom_price on assignment_articles)
    const getCustomerPriceForAa = (articleId: string): number => {
      const matchingAa = myArticles.find(aa => aa.article_id === articleId)
      if (matchingAa?.custom_price != null) return matchingAa.custom_price
      return getCustomerPrice(articleId)
    }

    // Provision percentage for this customer
    const provisionPercent = (customer?.provision_partner_id && partnerMap[customer.provision_partner_id] != null)
      ? partnerMap[customer.provision_partner_id] / 100
      : 0

    // Calculate revenue, costs, and provision PER TG from their reported articles
    let customerRevenue = 0
    let ueRevenue = 0
    let adminRevenue = 0
    let ueProvisionBase = 0
    let adminProvisionBase = 0
    let tgCosts = 0
    let adminTgCosts = 0
    const costsBySubcontractor: Record<string, number> = {}
    const revenueBySubcontractor: Record<string, number> = {}

    for (const tgReport of myTgReports) {
      const sub = tgReport.subcontractors as any
      const isAdmin = sub?.is_admin === true
      const tgName = (sub?.name as string) || 'Okänd'
      const tgPriceListId = sub?.price_list_id || defaultSubcontractorPriceListId
      const tgId = tgReport.subcontractor_id

      const reportedArticles = (tgReport.reported_articles as any[]) || []

      for (const ra of reportedArticles) {
        if (ra.is_expense) {
          // Expenses are pass-through (invoiced at cost) — exclude from statistics
          continue
        }

        if (!ra.article_id) continue
        const quantity = ra.actual_quantity || 0

        // Revenue: customer price × reported quantity
        const custPrice = getCustomerPriceForAa(ra.article_id)
        const lineRevenue = quantity * custPrice
        customerRevenue += lineRevenue
        revenueBySubcontractor[tgName] = (revenueBySubcontractor[tgName] || 0) + lineRevenue
        if (isAdmin) {
          adminRevenue += lineRevenue
          if (articleProvisionMap[ra.article_id] || adminArticleProvisionMap[ra.article_id]) {
            adminProvisionBase += lineRevenue
          }
        } else {
          ueRevenue += lineRevenue
          if (articleProvisionMap[ra.article_id] || adminArticleProvisionMap[ra.article_id]) {
            ueProvisionBase += lineRevenue
          }
        }

        // Cost: TG price × reported quantity
        const matchingAa = myArticles.find(aa => aa.article_id === ra.article_id)
        const tgPrices = (matchingAa?.subcontractor_prices as Record<string, number>) || {}

        let unitPrice = 0
        if (tgPrices[tgId] != null) {
          unitPrice = tgPrices[tgId]
        } else if (tgPriceListId && pricesByList[tgPriceListId]?.[ra.article_id] != null) {
          unitPrice = pricesByList[tgPriceListId][ra.article_id]
        } else if (defaultSubcontractorPriceListId && pricesByList[defaultSubcontractorPriceListId]?.[ra.article_id] != null) {
          unitPrice = pricesByList[defaultSubcontractorPriceListId][ra.article_id]
        }

        const lineCost = quantity * unitPrice
        costsBySubcontractor[tgName] = (costsBySubcontractor[tgName] || 0) + lineCost
        if (isAdmin) {
          adminTgCosts += lineCost
        } else {
          tgCosts += lineCost
        }
      }
    }

    // Add admin-reported articles to egenregi revenue (centrally delivered items)
    const adminArticles = (assignment.admin_reported_articles as any[]) || []
    for (const ra of adminArticles) {
      if (ra.is_expense) {
        // Expenses are pass-through (invoiced at cost) — exclude from statistics
        continue
      }

      if (!ra.article_id) continue

      const unitPrice = getCustomerPrice(ra.article_id)
      const lineTotal = (ra.actual_quantity || 0) * unitPrice
      customerRevenue += lineTotal
      adminRevenue += lineTotal

      if (adminArticleProvisionMap[ra.article_id]) {
        adminProvisionBase += lineTotal
      }
    }

    // Calculate provisions
    const totalProvisionBase = ueProvisionBase + adminProvisionBase
    const provisionAmount = totalProvisionBase * provisionPercent
    const ueProvision = ueProvisionBase * provisionPercent
    const adminProvision = adminProvisionBase * provisionPercent

    // Profit based on UE costs only (admin costs shown separately)
    const profit = customerRevenue - tgCosts - provisionAmount
    const marginPercent = customerRevenue > 0 ? (profit / customerRevenue) * 100 : 0

    // Collect subcontractor names for this assignment
    const subcontractorNames = myTgReports
      .map(tg => (tg.subcontractors as any)?.name as string)
      .filter(Boolean)
      .filter((name, i, arr) => arr.indexOf(name) === i)

    const adminSubcontractorNames = myTgReports
      .filter(tg => (tg.subcontractors as any)?.is_admin === true)
      .map(tg => (tg.subcontractors as any)?.name as string)
      .filter(Boolean)
      .filter((name, i, arr) => arr.indexOf(name) === i)

    result.push({
      assignment_id: assignment.id,
      assignment_number: assignment.assignment_number,
      title: assignment.title,
      start_date: assignment.start_date,
      customer_name: customer?.full_name || 'Okänd kund',
      customer_revenue: customerRevenue,
      ue_revenue: ueRevenue,
      admin_revenue: adminRevenue,
      tg_costs: tgCosts,
      admin_tg_costs: adminTgCosts,
      provision_amount: provisionAmount,
      ue_provision: ueProvision,
      admin_provision: adminProvision,
      profit,
      margin_percent: marginPercent,
      subcontractor_names: subcontractorNames,
      admin_subcontractor_names: adminSubcontractorNames,
      costs_by_subcontractor: costsBySubcontractor,
      revenue_by_subcontractor: revenueBySubcontractor,
    })
  }

  // 9. Aggregate totals
  const totalRevenue = result.reduce((sum, a) => sum + a.customer_revenue, 0)
  const totalTgCosts = result.reduce((sum, a) => sum + a.tg_costs, 0)
  const totalAdminTgCosts = result.reduce((sum, a) => sum + a.admin_tg_costs, 0)
  const totalProvisions = result.reduce((sum, a) => sum + a.provision_amount, 0)
  const totalProfit = totalRevenue - totalTgCosts - totalProvisions
  const averageMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0

  return {
    total_revenue: totalRevenue,
    total_tg_costs: totalTgCosts,
    total_admin_tg_costs: totalAdminTgCosts,
    total_provisions: totalProvisions,
    total_profit: totalProfit,
    average_margin: averageMargin,
    assignment_count: result.length,
    assignments: result,
  }
}

function emptyPeriod(): PeriodStatistics {
  return {
    total_revenue: 0,
    total_tg_costs: 0,
    total_admin_tg_costs: 0,
    total_provisions: 0,
    total_profit: 0,
    average_margin: 0,
    assignment_count: 0,
    assignments: [],
  }
}

/**
 * Fetch outstanding amounts independent of any date filter:
 *   1. What we owe non-admin subcontractors for reported work where we
 *      have not yet received their invoice (tg_invoice_status = 'ej_fakturerad').
 *   2. What we still need to invoice customers for, based on UE-reported work
 *      on assignments where customer_invoice_status = 'ej_fakturerad'.
 *
 * Egenregi (admin) work is excluded from both reports.
 */
export async function fetchOutstandingReports(): Promise<OutstandingReports> {
  // 1. Fetch all UE reports that are godkand/avsagt on slutford/attesterad assignments,
  //    join with assignments + customers, filter to non-admin subcontractors only.
  const { data: tgReports, error: tgError } = await supabase
    .from('assignment_subcontractors')
    .select(`
      assignment_id,
      subcontractor_id,
      reported_articles,
      tg_invoice_status,
      subcontractors!inner (
        id,
        name,
        price_list_id,
        is_admin
      ),
      assignments!inner (
        id,
        assignment_number,
        title,
        start_date,
        status,
        customer_invoice_status,
        customer_id,
        customers (
          id,
          full_name,
          price_list_id
        )
      )
    `)
    .in('tg_status', ['godkand', 'avsagt'])
    .in('assignments.status', ['slutford', 'attesterad'])
    .eq('subcontractors.is_admin', false)

  if (tgError) {
    console.error('Outstanding: Error fetching tg reports', tgError)
    throw tgError
  }

  if (!tgReports || tgReports.length === 0) {
    return emptyOutstanding()
  }

  // 2. Collect assignment IDs and fetch assignment_articles for price overrides.
  const assignmentIds = Array.from(new Set(tgReports.map(r => r.assignment_id)))

  const { data: assignmentArticles } = await supabase
    .from('assignment_articles')
    .select('id, assignment_id, article_id, custom_price, subcontractor_prices')
    .in('assignment_id', assignmentIds)

  // Group articles by assignment for fast lookup.
  const articlesByAssignment: Record<string, typeof assignmentArticles> = {}
  for (const aa of assignmentArticles || []) {
    if (!articlesByAssignment[aa.assignment_id]) {
      articlesByAssignment[aa.assignment_id] = []
    }
    articlesByAssignment[aa.assignment_id]!.push(aa)
  }

  // 3. Fetch default price lists.
  const { data: defaultPriceLists } = await supabase
    .from('price_lists')
    .select('id, type')
    .eq('is_default', true)
    .eq('is_active', true)

  const defaultCustomerPriceListId = defaultPriceLists?.find(pl => pl.type === 'customer')?.id
  const defaultSubcontractorPriceListId = defaultPriceLists?.find(pl => pl.type === 'subcontractor')?.id

  // 4. Collect all price list IDs we need.
  const allPriceListIds = new Set<string>()
  if (defaultCustomerPriceListId) allPriceListIds.add(defaultCustomerPriceListId)
  if (defaultSubcontractorPriceListId) allPriceListIds.add(defaultSubcontractorPriceListId)
  for (const r of tgReports) {
    const sub = r.subcontractors as any
    const assignment = r.assignments as any
    const customer = assignment?.customers as any
    if (sub?.price_list_id) allPriceListIds.add(sub.price_list_id)
    if (customer?.price_list_id) allPriceListIds.add(customer.price_list_id)
  }

  // 5. Fetch all prices in one query.
  const pricesByList: Record<string, Record<string, number>> = {}
  if (allPriceListIds.size > 0) {
    const { data: priceItems } = await supabase
      .from('price_list_articles')
      .select('price_list_id, article_id, price_excl_vat')
      .in('price_list_id', Array.from(allPriceListIds))

    for (const item of priceItems || []) {
      if (!pricesByList[item.price_list_id]) {
        pricesByList[item.price_list_id] = {}
      }
      pricesByList[item.price_list_id][item.article_id] = item.price_excl_vat
    }
  }

  // 6. Walk reports, computing per-line tgCost and customerRevenue.
  //    Aggregate into per-UE and per-customer groups.
  const subGroups = new Map<string, OutstandingSubcontractorGroup>()
  const subAssignmentTotals = new Map<string, Map<string, number>>() // subId → assignmentId → amount

  const customerGroups = new Map<string, OutstandingCustomerGroup>()
  const customerAssignmentTotals = new Map<string, Map<string, number>>() // custId → assignmentId → amount
  const assignmentMeta = new Map<string, { assignment_number: number | null; title: string; start_date: string; customer_name: string }>()

  for (const report of tgReports) {
    const sub = report.subcontractors as any
    const assignment = report.assignments as any
    const customer = assignment?.customers as any
    if (!sub || !assignment || !customer) continue

    const tgId = report.subcontractor_id
    const tgPriceListId = sub.price_list_id || defaultSubcontractorPriceListId
    const customerPriceListId = customer.price_list_id || defaultCustomerPriceListId

    const myArticles = articlesByAssignment[assignment.id] || []

    const getCustomerPriceForAa = (articleId: string): number => {
      const matchingAa = myArticles.find(aa => aa.article_id === articleId)
      if (matchingAa?.custom_price != null) return matchingAa.custom_price
      if (customerPriceListId && pricesByList[customerPriceListId]?.[articleId] != null) {
        return pricesByList[customerPriceListId][articleId]
      }
      if (defaultCustomerPriceListId && pricesByList[defaultCustomerPriceListId]?.[articleId] != null) {
        return pricesByList[defaultCustomerPriceListId][articleId]
      }
      return 0
    }

    const getTgUnitPrice = (articleId: string): number => {
      const matchingAa = myArticles.find(aa => aa.article_id === articleId)
      const tgPrices = (matchingAa?.subcontractor_prices as Record<string, number>) || {}
      if (tgPrices[tgId] != null) return tgPrices[tgId]
      if (tgPriceListId && pricesByList[tgPriceListId]?.[articleId] != null) {
        return pricesByList[tgPriceListId][articleId]
      }
      if (defaultSubcontractorPriceListId && pricesByList[defaultSubcontractorPriceListId]?.[articleId] != null) {
        return pricesByList[defaultSubcontractorPriceListId][articleId]
      }
      return 0
    }

    let lineTgCostSum = 0
    let lineCustomerRevenueSum = 0

    const reportedArticles = (report.reported_articles as any[]) || []
    for (const ra of reportedArticles) {
      if (ra.is_expense) continue
      if (!ra.article_id) continue
      const quantity = ra.actual_quantity || 0
      lineTgCostSum += quantity * getTgUnitPrice(ra.article_id)
      lineCustomerRevenueSum += quantity * getCustomerPriceForAa(ra.article_id)
    }

    if (lineTgCostSum === 0 && lineCustomerRevenueSum === 0) continue

    // Cache assignment meta once per assignment.
    if (!assignmentMeta.has(assignment.id)) {
      assignmentMeta.set(assignment.id, {
        assignment_number: assignment.assignment_number ?? null,
        title: assignment.title || 'Okänt uppdrag',
        start_date: assignment.start_date,
        customer_name: customer.full_name || 'Okänd kund',
      })
    }

    // a) Outstanding to pay this UE — only if their invoice is not yet received.
    if (report.tg_invoice_status === 'ej_fakturerad' && lineTgCostSum > 0) {
      if (!subGroups.has(tgId)) {
        subGroups.set(tgId, {
          subcontractor_id: tgId,
          subcontractor_name: sub.name || 'Okänd UE',
          total_excl_vat: 0,
          assignment_count: 0,
          assignments: [],
        })
        subAssignmentTotals.set(tgId, new Map())
      }
      const group = subGroups.get(tgId)!
      group.total_excl_vat += lineTgCostSum
      const perAssignment = subAssignmentTotals.get(tgId)!
      perAssignment.set(assignment.id, (perAssignment.get(assignment.id) || 0) + lineTgCostSum)
    }

    // b) Outstanding to invoice customer — only if customer not yet invoiced.
    if (assignment.customer_invoice_status === 'ej_fakturerad' && lineCustomerRevenueSum > 0) {
      const customerId = customer.id
      if (!customerGroups.has(customerId)) {
        customerGroups.set(customerId, {
          customer_id: customerId,
          customer_name: customer.full_name || 'Okänd kund',
          total_excl_vat: 0,
          assignment_count: 0,
          assignments: [],
        })
        customerAssignmentTotals.set(customerId, new Map())
      }
      const group = customerGroups.get(customerId)!
      group.total_excl_vat += lineCustomerRevenueSum
      const perAssignment = customerAssignmentTotals.get(customerId)!
      perAssignment.set(assignment.id, (perAssignment.get(assignment.id) || 0) + lineCustomerRevenueSum)
    }
  }

  // 7. Materialize per-assignment detail rows for each group.
  const buildDetails = (assignmentTotals: Map<string, number>): OutstandingAssignmentDetail[] => {
    const rows: OutstandingAssignmentDetail[] = []
    for (const [assignmentId, amount] of assignmentTotals) {
      const meta = assignmentMeta.get(assignmentId)
      if (!meta) continue
      rows.push({
        assignment_id: assignmentId,
        assignment_number: meta.assignment_number,
        title: meta.title,
        start_date: meta.start_date,
        customer_name: meta.customer_name,
        amount_excl_vat: amount,
      })
    }
    rows.sort((a, b) => b.start_date.localeCompare(a.start_date))
    return rows
  }

  for (const [tgId, group] of subGroups) {
    group.assignments = buildDetails(subAssignmentTotals.get(tgId)!)
    group.assignment_count = group.assignments.length
  }
  for (const [custId, group] of customerGroups) {
    group.assignments = buildDetails(customerAssignmentTotals.get(custId)!)
    group.assignment_count = group.assignments.length
  }

  const subGroupsArr = Array.from(subGroups.values()).sort((a, b) => b.total_excl_vat - a.total_excl_vat)
  const customerGroupsArr = Array.from(customerGroups.values()).sort((a, b) => b.total_excl_vat - a.total_excl_vat)

  return {
    toPaySubcontractors: {
      groups: subGroupsArr,
      total: subGroupsArr.reduce((s, g) => s + g.total_excl_vat, 0),
      assignment_count: subGroupsArr.reduce((s, g) => s + g.assignment_count, 0),
    },
    toInvoiceCustomers: {
      groups: customerGroupsArr,
      total: customerGroupsArr.reduce((s, g) => s + g.total_excl_vat, 0),
      assignment_count: customerGroupsArr.reduce((s, g) => s + g.assignment_count, 0),
    },
  }
}

function emptyOutstanding(): OutstandingReports {
  return {
    toPaySubcontractors: { groups: [], total: 0, assignment_count: 0 },
    toInvoiceCustomers: { groups: [], total: 0, assignment_count: 0 },
  }
}
